/**
 * PHARMAKON — Cross-module X · XWP-18 — Caisse libre multiwindow, pure helpers (no React, no I/O).
 *
 * The view model of a window (server DTO + the local overlay of the multiwindow store), its
 * totals on the shared pricing engine (the server's own maths: integer centimes, one remise
 * engine), the tab summary « Fenêtre 1 · BENALI · 3 articles · 1 250,00 DA », the storage keys of
 * the login session and of the per-tab mirror, and the reading of a refusal (409 « Stock
 * insuffisant — 2 disponibles » → « Ajuster à 2 »). The server stays the authority: these
 * figures are an immediate preview, the sale is priced again when the window is validated.
 */
import { CAISSE_FENETRES, CLE_LIGNE_FENETRE } from "@pharmakon/shared/constants/caisse-fenetres.js";
import { MODE_REMISE_VENTE, totauxVente } from "@pharmakon/shared/pricing/remise-vente.js";
import { versCentimes, versDinars, versTexteDecimal, multiplier } from "@pharmakon/shared/pricing/money.js";
import { resoudrePrixVente } from "@pharmakon/shared/pricing/prix-vente.js";
import { prixDeLigneRecherche } from "./prix-vente.js";

/** localStorage key of the caissier login session id (per user, per browser — XD-39). */
export const cleSession = (userId) => `pharmakon.caisse.session:${userId}`;
/** sessionStorage key of the multiwindow mirror (per user, per tab — instant restore on reload). */
export const cleMiroir = (userId) => `pharmakon.caisse.fenetres:${userId}`;

/** A basket line key the SPA proposes (optimistic add) — the server accepts it as is. */
export function nouvelleCleLigne(rand = Math.random) {
  const corps = Array.from({ length: 14 }, () => "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(rand() * 36)]).join("");
  const cle = `C${Date.now().toString(36)}${corps}`.slice(0, CLE_LIGNE_FENETRE.max);
  return cle;
}

/** A decimal price (string / number) → integer centimes, null when absent. */
const centimesOuNull = (prix) => (prix === undefined || prix === null || prix === "" ? null : versCentimes(prix));

/**
 * The amount (centimes) shown for a line — the price the sale charges, read from the window DTO (X
 * XWP-77 / XWP-80, XD-228): each held lot's `prix_unitaire` (the one resolver on that lot — a lot
 * priced 0 DA is charged `Produit.ppa`), else the line's own `prix_unitaire` (the FEFO head while it
 * holds nothing yet, or the optimistic price of `prixOptimiste`); a secours line its confirmed counter
 * price (X XWP-37, XMD-CP-02). Never re-derived from `ppa` / `prix_vente_ppa` here: a line of unknown
 * price is unknown (null — « — », the validation answers 422 PRIX_VENTE_INCONNU), never 0 DA.
 */
export function montantLigneCentimes(ligne) {
  if (ligne.secours) {
    const prix = centimesOuNull(ligne.prix_unitaire) ?? centimesOuNull(ligne.secours.prix_confirme);
    return prix === null ? null : multiplier(prix, ligne.quantite);
  }
  if (Array.isArray(ligne.lots) && ligne.lots.length) {
    const prixLots = ligne.lots.map((l) => centimesOuNull(l.prix_unitaire));
    if (prixLots.some((c) => c === null)) return null;
    const tenu = ligne.lots.reduce((s, l) => s + l.quantite, 0);
    const montantTenu = ligne.lots.reduce((s, l, i) => s + multiplier(prixLots[i], l.quantite), 0);
    if (tenu === ligne.quantite) return montantTenu;
    // a quantity not yet committed: priced at the line's mean lot price meanwhile
    return tenu > 0 ? Math.round((montantTenu * ligne.quantite) / tenu) : 0;
  }
  const prix = centimesOuNull(ligne.prix_unitaire);
  return prix === null ? null : multiplier(prix, ligne.quantite);
}

const SANS_PRIX = Object.freeze({ prix_unitaire: null, source_prix: null });

/**
 * The price of an optimistic line (« Réservation… ») until the server answers — the same price the
 * window DTO will carry (XD-228):
 *   · a secours line — its confirmed counter price (SAISI_SECOURS);
 *   · an explicit lot (lot scan, vignette) — that lot by the one resolver (the lot object handed by
 *     the caller, else the search row's own lot of that id); a lot the SPA does not hold is unknown;
 *   · no lot (FEFO) — the search row's `prix_vente` (the FEFO head the server holds first);
 *   · anything else (a product object without the server's price) — unknown until the server answers.
 * @param {{ produit: object, stock_lot_id?: string|null, lot?: object|null, secours?: object|null }} p
 * @returns {{ prix_unitaire: string|null, source_prix: string|null }}
 */
export function prixOptimiste({ produit, stock_lot_id = null, lot = null, secours = null }) {
  const prix = (r) => (r.centimes === null ? { ...SANS_PRIX } : { prix_unitaire: versTexteDecimal(r.centimes), source_prix: r.source });
  if (secours) return prix(resoudrePrixVente({ secours }));
  if (stock_lot_id) {
    const tenu = lot && lot.id === stock_lot_id ? lot : (produit?.stock_lots ?? []).find((l) => l.id === stock_lot_id) ?? null;
    return tenu ? prix(resoudrePrixVente({ lot: tenu, produit })) : { ...SANS_PRIX };
  }
  if (lot) return prix(resoudrePrixVente({ lot, produit }));
  const pv = prixDeLigneRecherche(produit);
  return pv.montant === null ? { ...SANS_PRIX } : { prix_unitaire: pv.montant, source_prix: pv.source };
}

/**
 * The lines the till shows for a window: the server lines with the pending local quantities, the
 * optimistic / refused additions after them, each with its state.
 *
 * @param {object|null} fenetre  the server DTO of the window
 * @param {object} [vue]         the store overlay { quantites, attente, erreurs, nouvelles, meta }
 */
export function lignesAffichees(fenetre, vue = {}) {
  if (!fenetre) return [];
  const quantites = vue.quantites ?? {};
  const attente = vue.attente ?? {};
  const erreurs = vue.erreurs ?? {};
  const meta = vue.meta ?? {};
  const serveur = (fenetre.lignes ?? []).map((l) => {
    const quantite = quantites[l.cle] ?? l.quantite;
    return {
      ...l,
      ...(meta[l.cle] ?? {}),
      quantite,
      quantite_serveur: l.quantite,
      etat: erreurs[l.cle] ? "erreur" : attente[l.cle] ?? (quantite !== l.quantite ? "maj" : "ok"),
      erreur: erreurs[l.cle] ?? null,
    };
  });
  const nouvelles = (vue.nouvelles ?? []).map((l) => ({
    ...l,
    ...(meta[l.cle] ?? {}),
    lots: [],
    reserve: 0,
    quantite_serveur: 0,
    etat: erreurs[l.cle] ? "erreur" : "reservation",
    erreur: erreurs[l.cle] ?? null,
  }));
  return [...serveur, ...nouvelles].map((l) => {
    const c = montantLigneCentimes(l);
    return { ...l, montant: c === null ? null : versDinars(c) };
  });
}

/**
 * The window's totals (preview) on the shared engine: brut, remise, total — the lines being
 * reserved or refused are not counted (they are not in the sale yet).
 *
 * @param {Array} lignes            from lignesAffichees
 * @param {{ mode: string, valeur: number }|null} remise  the window remise
 * @param {number|null} plafondPct  the server ceiling (référentiel), null while unknown
 */
export function totauxFenetre(lignes, remise, plafondPct) {
  const comptees = lignes.filter((l) => l.etat !== "reservation" && l.etat !== "erreur");
  // a line of unknown price (a secours line held with no price at all) adds nothing to the preview
  const prix = comptees.map((l) => ({ prix_unitaire_ppa: versDinars(montantLigneCentimes({ ...l, quantite: l.quantite }) ?? 0), quantite: 1 }));
  const r =
    remise?.mode === MODE_REMISE_VENTE.MONTANT
      ? { mode: MODE_REMISE_VENTE.MONTANT, montant_centimes: versCentimes(remise.valeur ?? 0) }
      : remise?.mode === MODE_REMISE_VENTE.POURCENTAGE
        ? { mode: MODE_REMISE_VENTE.POURCENTAGE, pct_centiemes: Math.round(Number(remise.valeur ?? 0) * 100) }
        : { mode: null };
  const plafond = plafondPct == null ? 10000 : Math.round(Number(plafondPct) * 100);
  const t = totauxVente(prix, r, plafond);
  return {
    brut: versDinars(t.brut_centimes),
    remise: versDinars(t.remise_centimes),
    total: versDinars(t.total_centimes),
    erreur: t.erreur,
    articles: comptees.reduce((s, l) => s + l.quantite, 0),
  };
}

/**
 * The remise a window shows (XWP-19): the one being typed — the store overlay `vue.remise =
 * { valeur }`, not committed yet (`valeur` null = « being cleared ») — else the server's.
 * @returns {{ mode: string, valeur: number }|null}
 */
export function remiseAffichee(fenetre, vue) {
  if (vue?.remise) return vue.remise.valeur ?? null;
  return fenetre?.remise ?? null;
}

/** The client's display name of a window (« BENALI Karim »), or null. */
export function nomClient(client) {
  if (!client) return null;
  return [client.nom, client.prenom].filter(Boolean).join(" ") || null;
}

/**
 * The tab summary of a window: « Fenêtre 1 · BENALI · 3 articles · 1 250,00 DA ».
 * @param {{ rang: number|null, client?: object }} fenetre
 * @param {{ articles: number, total: number }} totaux
 * @param {(n: number) => string} formatMontant
 */
export function resumeOnglet(fenetre, totaux, formatMontant) {
  const parts = [`Fenêtre ${fenetre.rang ?? "—"}`];
  const nom = fenetre.client?.nom ?? null;
  if (nom) parts.push(nom);
  parts.push(`${totaux.articles} article${totaux.articles > 1 ? "s" : ""}`);
  parts.push(formatMontant(totaux.total));
  return parts.join(" · ");
}

/** The ACTIVE windows ordered by rang (the tab order, Maj+F1…F3). */
export function fenetresOrdonnees(fenetres) {
  return Object.values(fenetres ?? {})
    .filter((f) => f && f.rang != null)
    .sort((a, b) => a.rang - b.rang);
}

/** True when another window may be opened (F3) — the server enforces the same cap (XD-47). */
export const peutOuvrir = (fenetres) => fenetresOrdonnees(fenetres).length < CAISSE_FENETRES.max_actives;

/**
 * Read a server refusal: { code, message, quantite_possible? }. A stock refusal on a line becomes
 * the badge « Stock insuffisant — 2 disponibles » with the one-key fix « Ajuster à 2 ».
 */
export function lireRefus(e, repli = "Opération impossible") {
  const corps = e?.response?.data ?? e ?? {};
  const details = corps.details ?? {};
  return {
    code: corps.code ?? null,
    message: corps.message ?? e?.message ?? repli,
    quantite_possible: Number.isFinite(details.quantite_possible) ? details.quantite_possible : null,
    statut: details.statut ?? null,
    // XWP-22 — « Envoyer vente libre » stopped: the server's verification, the reason of each line
    verification: details.verification ?? null,
    violations: details.violations ?? corps.violations ?? null,
    interactions: details.interactions ?? corps.interactions ?? null,
  };
}

/** XWP-22 — a line imported from an instance CHIFA (« Envoyer vente libre »): its lot and quantity are the instance's. */
export const estLigneInstance = (ligne) => Boolean(ligne?.instance?.id);

/**
 * XWP-22 — the instances a window pays in vente libre, with the keys of their lines (Suppr on one of
 * them takes the whole instance out): `[{ id, numero_interne, cles: [...], quantite }]`, import order.
 */
export function instancesDeFenetre(fenetre) {
  const parId = new Map();
  for (const l of fenetre?.lignes ?? []) {
    if (!estLigneInstance(l)) continue;
    const g = parId.get(l.instance.id) ?? { id: l.instance.id, numero_interne: l.instance.numero_interne ?? null, cles: [], quantite: 0 };
    g.cles.push(l.cle);
    g.quantite += l.quantite;
    parId.set(l.instance.id, g);
  }
  return [...parId.values()];
}

/** The refusals that mean « this window is gone on the server » (expired, closed, replaced). */
export const estFenetrePerdue = (refus) =>
  refus.code === "FENETRE_CAISSE_NON_ACTIVE" || refus.code === "SESSION_CAISSE_REMPLACEE";
